// NotificationBell — header bell with unread badge + dropdown of last 10 items.
// Polls /api/shadow/social/notifications every 30 s when authenticated.
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Bell } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../i18n/LanguageContext";
import { tShadow } from "../../i18n/shadow-social";
import { UserAvatar } from "./UserAvatar";

import API from "../../lib/api";

const POLL_MS = 30000;

const NOTIF_COPY = {
  NEW_FOLLOWER: "notifNewFollower",
  LIKE_ANALYSIS: "notifLikeAnalysis",
  COMMENT_ANALYSIS: "notifCommentAnalysis",
  MENTION: "notifMention",
  BADGE_UNLOCKED: "notifBadgeUnlocked",
  NEW_ANALYSIS_FROM_FOLLOWING: "notifNewAnalysisFromFollowing",
  RANKING_CHANGE: "notifRankingChange",
  WYCKOFF_MAJOR_SETUP: "notifWyckoffMajorSetup",
  CYCLES_EVENT: "notifCyclesEvent",
  PRO_BROADCAST: "notifProBroadcast",
};

export const NotificationBell = ({ authed = true }) => {
  const { lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const fetchNotifs = useCallback(async () => {
    if (!authed) return;
    try {
      const { data } = await axios.get(
        `${API}/shadow/social/notifications?limit=10`,
        { withCredentials: true }
      );
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch {
      // silent — likely unauthenticated
    }
  }, [authed]);

  useEffect(() => {
    fetchNotifs();
    if (!authed) return;
    const id = setInterval(fetchNotifs, POLL_MS);
    return () => clearInterval(id);
  }, [fetchNotifs, authed]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const markAllRead = async () => {
    try {
      await axios.patch(
        `${API}/shadow/social/notifications/read-all`,
        {},
        { withCredentials: true }
      );
      setUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      /* noop */
    }
  };

  if (!authed) return null;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={tShadow("notificaciones", lang)}
        data-testid="notification-bell"
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-full text-white/75 transition-colors hover:bg-white/10 hover:text-white"
      >
        <Bell className="h-4 w-4" strokeWidth={1.8} />
        {unread > 0 && (
          <span
            data-testid="notification-bell-badge"
            style={{
              position: "absolute",
              top: 2,
              right: 2,
              minWidth: 16,
              height: 16,
              padding: "0 4px",
              borderRadius: 10,
              background: "#ff5577",
              color: "#fff",
              fontSize: 9,
              fontWeight: 800,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1.5px solid #07080f",
              lineHeight: 1,
            }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-bell-dropdown"
          style={{
            position: "absolute",
            right: 0,
            top: 44,
            width: 340,
            maxHeight: 460,
            overflow: "auto",
            background: "#0a0b16",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 14,
            boxShadow: "0 8px 32px rgba(0,0,0,0.55)",
            zIndex: 80,
          }}
        >
          <div
            style={{
              padding: "12px 14px",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, color: "#fff", letterSpacing: ".05em" }}>
              {tShadow("notificaciones", lang)}
            </span>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                data-testid="notification-mark-read"
                style={{
                  fontSize: 10,
                  color: "rgba(0,212,255,0.85)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {tShadow("marcarLeidas", lang)}
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <div
              style={{
                padding: "26px 18px",
                textAlign: "center",
                fontSize: 12,
                color: "rgba(255,255,255,0.4)",
              }}
            >
              {tShadow("sinNotif", lang)}
            </div>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {items.map((n) => {
                const copyKey = NOTIF_COPY[n.type] || "notificaciones";
                const actorName = n.actor?.name || n.actor?.username || "—";
                const isBroadcast = n.type === "PRO_BROADCAST";
                return (
                  <li
                    key={n.id}
                    data-testid={`notif-item-${n.id}`}
                    style={{
                      padding: "10px 14px",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      borderBottom: "1px solid rgba(255,255,255,0.04)",
                      background: n.read
                        ? "transparent"
                        : isBroadcast
                          ? "rgba(249,115,22,0.10)"
                          : "rgba(245,166,35,0.04)",
                    }}
                  >
                    {isBroadcast ? (
                      <div
                        style={{
                          width: 32, height: 32, borderRadius: "50%",
                          background: "rgba(249,115,22,0.18)",
                          display: "inline-flex", alignItems: "center",
                          justifyContent: "center", fontSize: 16,
                        }}
                      >
                        📢
                      </div>
                    ) : n.actor ? (
                      <UserAvatar user={n.actor} size="sm" />
                    ) : (
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          background: "rgba(245,166,35,0.15)",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 14,
                        }}
                      >
                        🏅
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {isBroadcast ? (
                        <>
                          <p style={{ fontSize: 12, fontWeight: 700, color: "#f97316", margin: 0, lineHeight: 1.35 }}>
                            {n.title || tShadow("notifProBroadcast", lang)}
                          </p>
                          {n.message && (
                            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.72)", margin: "3px 0 0", lineHeight: 1.4 }}>
                              {n.message.length > 140 ? `${n.message.slice(0, 140)}…` : n.message}
                            </p>
                          )}
                          {n.image_url && (
                            <img
                              src={n.image_url}
                              alt=""
                              style={{
                                marginTop: 6, width: "100%", maxHeight: 100,
                                objectFit: "cover", borderRadius: 6,
                                border: "1px solid rgba(255,255,255,0.06)",
                              }}
                            />
                          )}
                        </>
                      ) : (
                        <p style={{ fontSize: 12, color: "rgba(255,255,255,0.85)", margin: 0, lineHeight: 1.4 }}>
                          {n.actor && (
                            <span style={{ fontWeight: 700 }}>{actorName} </span>
                          )}
                          {tShadow(copyKey, lang)}
                        </p>
                      )}
                      <span style={{ fontSize: 10, color: "rgba(255,255,255,0.35)" }}>
                        {n.created_at?.slice(0, 16).replace("T", " ")}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <div
            style={{
              padding: "10px 14px",
              borderTop: "1px solid rgba(255,255,255,0.06)",
              textAlign: "center",
            }}
          >
            <Link
              to="/shadow/notificaciones"
              onClick={() => setOpen(false)}
              style={{ fontSize: 11, color: "rgba(0,212,255,0.8)", textDecoration: "none" }}
            >
              {tShadow("verPerfil", lang)} →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
